import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, TextFormat, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './foreigner-residence.reducer';

export const ForeignerResidence = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const foreignerResidenceList = useAppSelector(state => state.foreignerResidence.entities);
  const loading = useAppSelector(state => state.foreignerResidence.loading);
  const totalItems = useAppSelector(state => state.foreignerResidence.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="foreigner-residence-heading" data-cy="ForeignerResidenceHeading">
        <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.title">Foreigner Residences</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/foreigner-residence/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.createLabel">Create new Foreigner Residence</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {foreignerResidenceList && foreignerResidenceList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('recordNumber')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordNumber">Record Number</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('recordNumber')} />
                </th>
                <th className="hand" onClick={sort('createDate')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.createDate">Create Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('createDate')} />
                </th>
                <th className="hand" onClick={sort('recordDate')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.recordDate">Record Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('recordDate')} />
                </th>
                <th className="hand" onClick={sort('foreignerFullName')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.foreignerFullName">Foreigner Full Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('foreignerFullName')} />
                </th>
                <th className="hand" onClick={sort('nationality')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.nationality">Nationality</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('nationality')} />
                </th>
                <th className="hand" onClick={sort('foreignerPassportNo')}>
                  <Translate contentKey="foreignerResidenceApp.foreignerResidence.foreignerPassportNo">Foreigner Passport No</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('foreignerPassportNo')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {foreignerResidenceList.map((foreignerResidence, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/foreigner-residence/${foreignerResidence.id}`} color="link" size="sm">
                      {foreignerResidence.id}
                    </Button>
                  </td>
                  <td>{foreignerResidence.recordNumber}</td>
                  <td>
                    {foreignerResidence.createDate ? (
                      <TextFormat type="date" value={foreignerResidence.createDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {foreignerResidence.recordDate ? (
                      <TextFormat type="date" value={foreignerResidence.recordDate} format={APP_LOCAL_DATE_FORMAT} />
                    ) : null}
                  </td>
                  <td>
                    {[foreignerResidence.foreignerTitle, foreignerResidence.foreignerFullName].filter(Boolean).join(' ').toUpperCase()}
                  </td>
                  <td>
                    <Translate contentKey={`foreignerResidenceApp.Nationality.${foreignerResidence.nationality}`} />
                  </td>
                  <td>{foreignerResidence.foreignerPassportNo}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      {/*  
                      <Button
                        tag={Link}
                        to={`/foreigner-residence/${foreignerResidence.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      */}
                      <Button
                        tag={Link}
                        to={`/foreigner-residence/${foreignerResidence.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.manage">Manage</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/foreigner-residence/${foreignerResidence.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="foreignerResidenceApp.foreignerResidence.home.notFound">No Foreigner Residences found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={foreignerResidenceList && foreignerResidenceList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default ForeignerResidence;
